import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { InvitationInfo } from '@mlc/shared';
import { acceptInvitation, readInvitation } from './api';
import { navigate, paths } from './router';
import { Banner, Button, LogoLockup, TextInput } from './ui';

/**
 * L'arrivée d'un invité (US-103).
 *
 * **Le premier écran de quelqu'un qui ne connaît pas l'application**, et le
 * seul, avec le lien de partage et la réinitialisation, à s'ouvrir sans
 * session. Il tient en une page : qui l'invite, son prénom, un mot de passe à
 * choisir, et c'est tout — pas de conditions à cocher, pas de champ à remplir
 * qu'on ne lui a pas demandé.
 *
 * **Le prénom est modifiable.** Le propriétaire l'a saisi de mémoire, et un
 * livre de famille appelle les gens par leur prénom sur chaque recette : le
 * dernier moment pour le corriger sans avoir à chercher un écran de profil est
 * ici.
 *
 * **Un lien mort dit une seule chose.** Inconnu, périmé, déjà utilisé, annulé :
 * le serveur répond le même 410 dans tous les cas, et cet écran affiche la même
 * phrase. Il n'a rien à deviner, et surtout rien à révéler.
 */
export interface InvitationProps {
  /** Jeton lu dans l'URL. `null` si le lien a été tronqué en chemin. */
  jeton: string | null;
}

export function Invitation({ jeton }: InvitationProps): React.JSX.Element {
  return (
    /* Même composition que la connexion et que la réinitialisation : trois
       écrans qui se suivent, une seule largeur. */
    <main className="mx-auto flex min-h-full w-full max-w-form flex-col justify-center gap-6 px-6 py-12">
      <div className="flex w-full flex-col gap-5">
        <h1 className="mb-2 flex justify-center">
          <LogoLockup />
        </h1>
        {jeton === null ? <LienMort /> : <Accueil jeton={jeton} />}
      </div>
    </main>
  );
}

function LienMort(): React.JSX.Element {
  return (
    <>
      <Banner tone="danger">
        Ce lien n’est plus valable. Demandez à la personne qui vous a invité de vous en renvoyer un.
      </Banner>
      <Button type="button" variant="secondary" onClick={() => { navigate(paths.library); }}>
        Aller à la connexion
      </Button>
    </>
  );
}

function Accueil({ jeton }: { jeton: string }): React.JSX.Element {
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [mort, setMort] = useState(false);

  useEffect(() => {
    let vivant = true;
    readInvitation(jeton)
      .then((info) => { if (vivant) setInvitation(info); })
      // Toutes les raisons de refuser un lien se ressemblent ici, comme côté
      // serveur : il n'y a rien à distinguer et rien à dire de plus.
      .catch(() => { if (vivant) setMort(true); });
    return () => { vivant = false; };
  }, [jeton]);

  if (mort) return <LienMort />;
  if (invitation === null) {
    return <p className="text-center text-sm text-foreground-secondary">Un instant…</p>;
  }
  return <Formulaire jeton={jeton} invitation={invitation} onMort={() => { setMort(true); }} />;
}

function Formulaire(
  { jeton, invitation, onMort }: { jeton: string; invitation: InvitationInfo; onMort: () => void },
): React.JSX.Element {
  const [firstName, setFirstName] = useState(invitation.first_name);
  const [lastName, setLastName] = useState(invitation.last_name ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (enCours) return;
    setEnCours(true);
    setError(null);
    acceptInvitation(jeton, { password, first_name: firstName, last_name: lastName })
      .then(() => {
        // La session est ouverte par la réponse : un rechargement complet
        // plutôt qu'une navigation, pour que tout ce qui a été chargé sans
        // identité — l'état de session en cache, la barre de navigation —
        // reparte de zéro.
        window.location.assign(paths.library);
      })
      .catch((e: unknown) => {
        // Le lien a pu mourir entre l'affichage et l'envoi : quelqu'un l'a
        // ouvert avant, ou le propriétaire l'a annulé.
        if (e instanceof Error && e.message.includes('n’est plus valable')) { onMort(); return; }
        setError(e instanceof Error ? e.message : String(e));
        setEnCours(false);
      });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <p className="text-center text-sm text-foreground-secondary">
        <strong>{invitation.invite_par}</strong> vous invite dans son livre de recettes.
        Choisissez un mot de passe pour entrer.
      </p>
      {error !== null && <Banner tone="danger">{error}</Banner>}

      <div className="flex flex-wrap gap-2">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          Prénom
          <TextInput
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => { setFirstName(e.target.value); }}
          />
        </label>
        <label className="flex flex-1 flex-col gap-1 text-sm">
          <span>Nom <span className="text-foreground-muted">(facultatif)</span></span>
          <TextInput
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => { setLastName(e.target.value); }}
          />
        </label>
      </div>

      {/* L'adresse est affichée et non modifiable : c'est elle qui a reçu le
          lien, et la changer ici reviendrait à inviter quelqu'un d'autre. */}
      <p className="text-xs text-foreground-secondary">
        Vous vous connecterez avec <strong>{invitation.email}</strong>.
      </p>

      <label className="flex flex-col gap-1 text-sm">
        Mot de passe
        <TextInput
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); }}
        />
      </label>
      <p className="text-xs text-foreground-secondary">
        Huit caractères au minimum. Personne d’autre ne le connaîtra, pas même la personne qui vous invite.
      </p>

      <Button type="submit" variant="primary" disabled={enCours || password.length < 8 || firstName.trim() === ''}>
        Entrer dans le livre
      </Button>

      {/* **La phrase d'honnêteté** (§5), dite avant d'entrer et non après.
          Celui qui héberge l'instance possède le fichier de la base : il peut
          tout lire, quoi que dise l'interface. Le taire à quelqu'un à qui l'on
          demande de tenir un journal personnel serait le tromper. */}
      <p className="text-xs text-foreground-secondary">
        À savoir : vos recettes privées et votre journal ne sont visibles d’aucun autre membre. La personne qui
        héberge cette application, elle, a accès au fichier de données, donc à tout. Aucune application ne peut
        promettre le contraire.
      </p>
    </form>
  );
}
